import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import {
  SpreadsheetContext,
  SpreadsheetProvider,
  SpreadsheetTable,
} from "@gistapps/composite-spreadsheet";
import type {
  ColumnConfig,
  ColumnTypeConfig,
  Point,
  SpreadsheetRecord,
} from "@gistapps/composite-spreadsheet";
export type SpreadsheetTableHandle = {
  exportTable: () => void;
  clearTable: () => void;
  submitTable: () => Promise<SpreadsheetRecord[] | null>;
};

type ProxyBulkOrderTableProps = {
  columns: ColumnConfig[];
  columnTypeConfig?: ColumnTypeConfig;
  maxRowCount?: number;
  onCellActivated: (rowIndex: number, record: SpreadsheetRecord, columnKey?: string) => void;
};

type InnerHandle = {
  exportTable: () => void;
  clearTable: () => void;
  submitTable: () => Promise<SpreadsheetRecord[] | null>;
};

const ProxyBulkOrderTableInner = forwardRef<InnerHandle, {
  activeRowRef: React.MutableRefObject<number>;
}>(({ activeRowRef }, ref) => {

  const {
    exportSpreadsheet,
    clearSpreadsheetData,
    submitSpreadsheet,
    updateSpreadsheetCellData,
    columnMap,
  } = useContext(SpreadsheetContext);

  useImperativeHandle(ref, () => ({

    exportTable: () => {
      exportSpreadsheet("Gift-Card-Recipients");
    },

    clearTable: () => {
      clearSpreadsheetData();
    },

    submitTable: async (): Promise<SpreadsheetRecord[] | null> => {

      const result = await submitSpreadsheet();

      if (result.status === "success") {
        return result.data;
      }

      if (result.status === "error") {
        const invalidRows = (result as any).invalidRows ?? [];
        const message = invalidRows.length > 0
          ? `Please correct errors in the following rows: ${invalidRows.map((r: number) => r + 1).join(", ")}`
          : "Please correct all errors before submitting.";
        setTimeout(() => alert(message), 100);
      }

      return null;

    },

  }));

  useEffect(() => {

    const handleExternalFieldChange = (e: Event) => {

      const detail = (e as CustomEvent).detail;
      const field = detail.field as string;
      const value = detail.value;
      const row = activeRowRef.current;

      if (row < 0) return;

      const fieldKey = field.replaceAll(".", "__");
      const colIndex = columnMap.get(fieldKey) ?? columnMap.get(field);

      if (colIndex === undefined) return;

      updateSpreadsheetCellData(row, colIndex, { value: String(value ?? "") }, true);

    };

    document.addEventListener("gcp-field-changed", handleExternalFieldChange);

    return () => {
      document.removeEventListener("gcp-field-changed", handleExternalFieldChange);
    };

  }, [columnMap, updateSpreadsheetCellData, activeRowRef]);

  return null;

});

const ProxyBulkOrderTable = forwardRef<SpreadsheetTableHandle, ProxyBulkOrderTableProps>(
  (
    {
      columns,
      columnTypeConfig,
      maxRowCount,
      onCellActivated,
    },
    ref
  ) => {

    const innerRef = useRef<InnerHandle>(null);
    const allRecordsRef = useRef<SpreadsheetRecord[]>([]);
    const activeRowRef = useRef<number>(-1);

    useImperativeHandle(ref, () => ({
      exportTable: () => innerRef.current?.exportTable(),
      clearTable: () => innerRef.current?.clearTable(),
      submitTable: () => {
        const hasAnyData = allRecordsRef.current.some((record) =>
          Object.values(record).some((v) => v !== null && v !== undefined && String(v).trim() !== "")
        );
        if (!hasAnyData) {
          setTimeout(() => alert("Please add at least one recipient before submitting."), 100);
          return Promise.resolve(null);
        }
        return innerRef.current?.submitTable() ?? Promise.resolve(null);
      },
    }));

    const handleDataRecordsChange = (records: SpreadsheetRecord[]) => {
      allRecordsRef.current = records;
    };

    const handleActivate = (point: Point) => {
      activeRowRef.current = point.row;
      const record = allRecordsRef.current[point.row];
      if (record) {
        const columnKey = columns[point.column]?.key;
        onCellActivated(point.row, record, columnKey);
      }
    };

    return (
      <SpreadsheetProvider
        columns={columns}
        columnTypeConfig={columnTypeConfig}
        data={[]}
        maxRowCount={maxRowCount}
        onDataRecordsChange={handleDataRecordsChange}
        onActivate={handleActivate}
        allowAddRows
        stripedRows
        displayRowCompletion
      >
        <SpreadsheetTable />
        <ProxyBulkOrderTableInner
          ref={innerRef}
          activeRowRef={activeRowRef}
        />
      </SpreadsheetProvider>
    );

  }
);

export default ProxyBulkOrderTable;
